<template>
  <span
    class="mt-2 flex items-center space-x-3"
    :class="
      !attribute
        ? 'border border-red-400 text-red-400'
        : isContextNeeded && !isContextSet
          ? 'border border-yellow-300'
          : undefined
    "
    data-cy="attribute-item"
  >
    <span
      v-if="attribute"
      v-tooltip="attributeInformation"
      class="flex items-center space-x-3"
    >
      <img
        v-show="serviceAttributeImg"
        :src="serviceAttributeImg"
        class="h-6 w-6"
        :alt="attributeName"
        loading="lazy"
      />
      <span class="text-headline">
        {{ attributeName }}
      </span>
    </span>
    <span v-else>
      {{ t("COMMON.UNKNOWN") }} {{ t("ATTRIBUTE_BUILDER.ATTRIBUTE") }}
    </span>
    <equation-attribute-context
      v-if="isContextNeeded"
      :item="value"
      :attribute="attribute || null"
      :is-active="isActive"
    />
  </span>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import {
  SERVICE_APPLICATION_DATA,
  SERVICE_CUSTOM_ATTRIBUTES,
} from "@/helpers/constants";
import { getImageForService } from "@/helpers/services";
import { useAttributeBuilderServices } from "@/hooks/attributeBuilder";

import EquationAttributeContext from "@/components/customAttributes/formula/EquationAttributeContext.vue";

import type { BOP_CONSUMER_SCORE_MODELS } from "@/helpers/constants/experian";
import type {
  IBopConsumerScoreModelLevel,
  IRelativeDateRange,
  ISpecificDateRange,
  IStepAttribute,
} from "@/models/orchestration";
import type { PropType } from "vue";

const props = defineProps({
  value: {
    type: Object as PropType<IStepAttribute>,
    required: true,
  },
  isActive: {
    type: Boolean,
    default: false,
  },
});
const { getters } = useStore();
const { t } = useI18n();

const { getService, getServiceProviderDetails } = useAttributeBuilderServices();

const bopConsumerScoreModels = computed<typeof BOP_CONSUMER_SCORE_MODELS>(
  () => getters["options/experianBopConsumerScoreModels"]
);

const attribute = computed(() =>
  getService(props.value.id, props.value.context)
);

const serviceAttributeProviderInfo = computed(() =>
  getServiceProviderDetails(props.value.id)
);

const serviceAttributeImg = computed(() => {
  if (
    serviceAttributeProviderInfo.value?.category &&
    (
      [SERVICE_APPLICATION_DATA, SERVICE_CUSTOM_ATTRIBUTES] as string[]
    ).includes(serviceAttributeProviderInfo.value?.category)
  ) {
    return getImageForService(serviceAttributeProviderInfo.value?.category);
  } else {
    return getImageForService(serviceAttributeProviderInfo.value?.id);
  }
});

const attributeName = computed(() => attribute.value?.name || "");

const attributeServiceTree = computed(() => {
  return [
    serviceAttributeProviderInfo.value?.title,
    serviceAttributeProviderInfo.value?.name,
    serviceAttributeProviderInfo.value?.category,
  ]
    .filter(Boolean)
    .join("/");
});

const attributeContextValue = computed(() => {
  if (!props.value.context) {
    return "";
  }

  const scoreModel = (props.value.context?.value as IBopConsumerScoreModelLevel)
    ?.level;

  const scoreModelLabel =
    scoreModel && bopConsumerScoreModels.value[scoreModel];

  const { include_month_to_date, lookback_months } = props.value.context
    ?.value as IRelativeDateRange;

  const monthToDateLabel =
    include_month_to_date && t("ORCHESTRATION.MONTH_TO_DATE");

  const lastMonthsLabel =
    lookback_months &&
    t("ORCHESTRATION.LAST_NUMBER_MONTHS", {
      months: lookback_months,
    });

  const { from, through } = props.value.context?.value as ISpecificDateRange;
  const range = from && through && `${from.slice(0, 7)}/${through.slice(0, 7)}`;

  return [scoreModelLabel, monthToDateLabel, lastMonthsLabel, range]
    .filter(Boolean)
    .join(" ");
});

const attributeInformation = computed(() => {
  return [attributeServiceTree.value, attributeContextValue.value].join("\n");
});

const isContextNeeded = computed(() => {
  return (
    !!attribute.value?.options?.consumer_score_models_levels ||
    !!attribute.value?.options?.month_picker_range
  );
});

const isContextSet = computed(() => {
  return props.value.context !== undefined;
});
</script>
